/**
 * GrupMunicipalView component.
 * @module components/View/GrupMunicipalView
 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl';
import RenderBlocks from '@plone/volto/components/theme/View/RenderBlocks';
import { searchContent } from '@plone/volto/actions';
import { UniversalLink, Image, PreviewImage } from '@plone/volto/components';
import { BodyClass, hasBlocksData, flattenToAppURL } from '@plone/volto/helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { Card, Container, Grid } from 'semantic-ui-react';
import { keys, isEmpty, map, filter } from 'lodash';
import { getSocialNetworkIcon } from '../../helpers'


const GrupMunicipalView = (props) => {
    const { content } = props;
    const intl = useIntl();
    const dispatch = useDispatch();
    const carrecs = useSelector((state) => state.search.subrequests.carrecs?.items);
    const total = useSelector((state) => state.search.subrequests.carrecs?.total);

    const getCarrec = (carrecToken) => {
        if (!carrecToken || !Array.isArray(carrecs)) return null;
        const carrec = carrecs.find((c) => c.UID === carrecToken);
        return carrec?.title;
    }

    useEffect(() => {
        let contentFilter = {
            portal_type: ['Carrec'],
            sort_on: 'getObjPositionInParent',
            metadata_fields: ['UID'],
            b_size: 1000,
        };
        dispatch(searchContent(flattenToAppURL(content['@id']), contentFilter, 'carrecs'));
    }, [dispatch]);

    const formatPhone = (phone) => {
        let cleaned = phone.replace(/\s+/g, '').replace(/[()-]/g, '');
        if (!cleaned.startsWith('+')) {
            cleaned = `+34${cleaned}`;
        }
        return `tel:${cleaned}`;
    };

    const formatCarrecsCount = (count) => {
        return intl.formatMessage(
            {
                id: 'regidors',
                defaultMessage: `{count, plural,
                    one {# regidor}
                    other {# regidors}
                }`,
            },
            { count },
        );
    };

    return (
    <>
      <BodyClass className='grupmunicipal-view' />
      <BodyClass className='smallHeader' />
      <Container>
        <div className="grupMunicipal">

          {content.preview_image && (
          <Image
            className="grupMunicipal__logo"
            item={content}
            imageField="preview_image"
            alt=""
            />
          )}

          <div className='grupMunicipal__text'>            
            <dl className="grupMunicipal__details">
              {content.address && (
              <div className='grupMunicipal__details__item'>
                <dt className='fw-bold'><FormattedMessage id="Address" defaultMessage="Address" />:</dt>
                <dd>{content.address}</dd>
              </div>
              )}

              {content.telephone && (
              <div className="grupMunicipal__details__item">
                <dt className='fw-bold'><FormattedMessage id="Phone" defaultMessage="Phone" />:</dt>
                <dd><a href={formatPhone(content.telephone)}>{content.telephone}</a></dd>
              </div>
              )}

              {content.email && (
              <div className="grupMunicipal__details__item">
                <dt className='fw-bold'><FormattedMessage id="Email" defaultMessage="Email" />:</dt>
                <dd><a href={`mailto:${content.email}`}>{content.email}</a></dd>
              </div>
              )}

              {content.web && (
              <div className="grupMunicipal__details__item">
                <dt className='fw-bold'><FormattedMessage id="Website" defaultMessage="Website" />:</dt>
                <dd><a href={content.web}>{content.web}</a></dd>
              </div>
              )}

              {content.spokesperson && (
              <div className="grupMunicipal__details__item grupMunicipal__details__item--spoke">
                <dt className='fw-bold'><FormattedMessage id="Spokesperson" defaultMessage="Spokesperson" />:</dt>
                <dd>{getCarrec(content.spokesperson?.token)}</dd>
              </div>
              )}
              {content.deputy_spokesperson && (
              <div className="grupMunicipal__details__item">
                <dt className='fw-bold'><FormattedMessage id="Deputy Spokesperson" defaultMessage="Deputy Spokesperson" />:</dt>
                <dd>{getCarrec(content.deputy_spokesperson?.token)}</dd>
              </div>
              )}

              {!isEmpty(content.social_networks) && (
              <div className="d-flex mb-3 pt-2">
                <dt className='visually-hidden'><FormattedMessage id="Social networks" defaultMessage="Social networks" /></dt>
                <dd className='ms-0 flex-grow-1'>
                  <ul className="list-unstyled grupMunicipal__xxss">
                    {map(content?.social_networks?.items, (social_network, index) => (
                    <li className="grupMunicipal__xxss__item" key={index}>
                      <a className='grupMunicipal__xxss__link' href={social_network.link}>
                        <FontAwesomeIcon icon={getSocialNetworkIcon(social_network.social_network)} />
                        <span className="visually-hidden">{social_network.social_network}</span>
                      </a>
                    </li>
                    ))}
                  </ul>
                </dd>
              </div>
              )}
            </dl>
          </div>

        </div>

        {!isEmpty(carrecs) && (
            <>
            <h2 className="grupMunicipal__escons fs-2 text-center mb-3">{formatCarrecsCount(total)}</h2>
            <Grid>
              <Grid.Row as="ul" className='list-unstyled mt-0' columns="12">
                {carrecs.map((item) => (
                <Grid.Column key={item['@id']} as="li" mobile={12} tablet={6} computer={4} widescreen={3} largeScreen={3} className='mb-4'>
                  <article className='grupMunicipal__regidor position-relative'>

                    {item.image_field && (
                    <PreviewImage
                        className="grupMunicipal__regidor__image"
                        item={item}
                        alt=''
                        sizes="(max-width: 767px) 100vw, (max-width: 991px) 50vw, (max-width: 991px) 33vw, 25vw" />
                    )}

                    <h3 className='grupMunicipal__regidor__name'>
                      <UniversalLink href={flattenToAppURL(item['@id'])} className="grupMunicipal__regidor__link stretched-link">
                        {item?.title}
                      </UniversalLink>
                    </h3>
                    {item?.description && (<p className='grupMunicipal__regidor__carrec'>{item?.description}</p>)}
                    <span className='grupMunicipal__regidor__readMore'><FontAwesomeIcon icon={faPlus} /></span>
                  </article>
                </Grid.Column>
                ))}
              </Grid.Row>
            </Grid>
            </>
        )}

        {/* Volto Blocks */}
        {hasBlocksData(content) ? (
          <div className="userBlocks">
            <RenderBlocks {...props} />
          </div>
        ) : null}

      </Container>

    </>
    );
};

export default GrupMunicipalView;